import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { useProject } from "@/hooks/Network/useProject";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useSlug } from "@/hooks/useSlug";
import { communityGroupMutations } from "@/queries/community-groups/mutations";
import { useCommunityGroupQueries } from "@/queries/community-groups/queryOptions";
import { selfAccountMutations } from "@/queries/self-account/mutations";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import type { ApiQueryParams } from "@/types/api-types";

import { OnboardingHelpCategories, type OnboardingHelpCategoriesProps } from "./OnboardingHelpCategories";

export function OnboardingHelpCategoriesPage(): React.ReactNode {
  const { t } = useTranslation();
  useDocumentTitle(t("page.onboarding.help-categories.title"));
  const project = useProject();
  const slug = useSlug();
  const navigate = useNavigate();
  const communityGroupQueries = useCommunityGroupQueries();

  const query = {
    groupTypes: ["helpCategory"],
    Limit: 10000,
  } satisfies ApiQueryParams<"getGroupsV2">;

  const { data: helpCategories = [], isLoading, error } = useQuery(communityGroupQueries.list(query));

  const followMutation = communityGroupMutations.useFollowGroup(query);
  const unfollowMutation = communityGroupMutations.useUnfollowGroup(query);
  const markOnboardedMutation = selfAccountMutations.useMarkOnboarded();

  const handleMarkOnboarded = () => {
    markOnboardedMutation.mutate(undefined, {
      onSuccess: () => {
        void navigate(routes.communityFeed.list({ slug }));
      },
    });
  };

  if (error) {
    return <ErrorPage error={error} />;
  }

  if (isLoading) {
    return <FullSizeLoader withPadding />;
  }

  const helpCategoriesProps: OnboardingHelpCategoriesProps = {
    helpCategories: helpCategories ?? [],
    project,
    onFollow: followMutation.mutate,
    onUnfollow: unfollowMutation.mutate,
    onMarkOnboarded: handleMarkOnboarded,
    isMarkingAsOnboarded: markOnboardedMutation.isPending,
  };

  return <OnboardingHelpCategories {...helpCategoriesProps} />;
}
